import { DragDropProvider } from "@dnd-kit/react";
import { isSortableOperation } from "@dnd-kit/react/sortable";
import { Divider } from "antd";
import React, { useEffect } from 'react';
import { Footer } from "../";
import Note from "../../../../../components/Popup/PopupOpInfo/_component/Note";
import { getStore } from "../../../../../util/store/store";
import { ACTION_TYPE, CERTAIN_TYPE, OPERATION_TYPE, serviceTask } from "../../../../../util/varibles/constants";
import { convertNumberTimeToStringTime } from "../../../../../util/varibles/global";
import { IService, ITaskService, TOperation } from "../../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../../util/varibles/message";
import { UnitModel } from "../../../BiologyBrowser/constants";
import { selectPlan } from "../../../constants";
import { callEventEditOp, getEditOp, IUnitWhenEdit, useEditOp } from "../EditOperationContext";
import AddChecklist from "../_components/AddChecklist";
import AddNote from "../_components/AddNote";
import AttachFile from "../_components/AttachFile";
import Certain from "../_components/Certain";
import Duration from "../_components/Duration";
import { ErrorName } from "../_components/ErrorOfOperation";
import SelectTime from "../_components/SelectTime";
import ActivityLog from "./ActivityLog";
import GroupTask from "./GroupTask";
import { initTask } from "./constants";

const ContainerFooter: React.FC = () => {
    const [loadingSubmit, set] = useEditOp(state => state.loadingSubmit);
    const [tasks = []] = useEditOp(state => state.operation?.tasks);
    const get = getEditOp();

    const handleSubmit = () => {
        const { editMode, serviceTasks: { data: serviceTasks }, operation, vessel, units, onSave } = get();
        const { tasks = [] } = operation || {} as IService;
        let isError = false;
        if (!operation || !vessel)
            return;

        const ids = new Set<string>([]);
        let locationEmpty = false;
        const tasksAfterCheck = tasks.map(item => {
            const { type, duration, sub_operations, location_id } = item;
            const error: { [id: string]: { message: string } } = {};
            if (!location_id || location_id.length === 0) {
                locationEmpty = true;
            }
            if (!type)
                error.type = { message: notify[NotifyCode.E29](['Task']) }
            else {
                const isExist = serviceTasks.some(item => item.id === type);
                if (!isExist)
                    error.type = { message: notify[NotifyCode.E29](['Task']) }
            }
            if (!duration)
                error[ErrorName.Duration] = { message: notify[NotifyCode.E29](['Duration']) }
            if (Object.keys(error).length > 0) {
                isError = true;
                return { ...item, error }
            }
            sub_operations.forEach(item => ids.add(item.id));
            return item;
        })
        if (locationEmpty) {
            notify.error(NotifyCode.E29)(['Location'])
            return;
        }

        if (isError) {
            set({ operation: { ...operation, tasks: tasksAfterCheck as any } })
            return;
        }

        const { id = 0 } = operation || {};
        const { activity_log } = get();
        const newOp = {
            operation: { ...operation, sub_operations: Array.from(ids).map(id => units[id]) },
            vessel,
            activity_log
        }

        onSave(editMode === ACTION_TYPE.EDIT ? { update: { [id]: newOp } } : { add: { [id]: newOp } })
    };

    return <div className="mt-auto">
        <Footer {...{
            loading: loadingSubmit,
            disableSave: loadingSubmit || tasks.length === 0,
            handleSubmit: handleSubmit,
        }} />
    </div>
}

const Service: React.FC = () => {
    const [operation, set] = useEditOp(state => state.operation);
    const get = getEditOp();

    const { handeUpdateOp } = callEventEditOp()

    useEffect(() => {
        const { tenant_id: userTenantId } = getStore().login.user;

        const popupEdit = selectPlan.popupEdit(getStore());
        const { values, editMode, location } = popupEdit || {};
        const { store = {} } = values || {};
        const sites = selectPlan.sites(getStore());
        const { operation, activity_log } = get();
        const { location_id } = operation?.tasks?.[0] || { location_id: undefined };
        const { site_id: newSiteId = location_id || location?.id } = operation?.sub_operations[0] || {};
        const site = sites.find(site => site.id === newSiteId);

        const { tenant_id = userTenantId, certain_mode = CERTAIN_TYPE.NONE, files = [] } = operation || {} as IService;

        const tasks = editMode === ACTION_TYPE.CREATE
            ? (location
                ? operation?.tasks || []
                : Object.keys(store || {}).reduce((rs: ITaskService[], key) => {
                    const { site_id: siteId = "", site_name = "" } = store[key];
                    if (siteId !== newSiteId)
                        return rs;
                    rs.push(initTask({ ...store[key], location_id: siteId, location_name: site_name }))
                    return rs
                }, []))
            : operation?.tasks || [];

        if (tasks.length === 0) {
            tasks.push(initTask({ location_id: location?.id || "", location_name: location?.name || "" }))
        }

        const op = {
            ...operation || {},
            parallel: true,
            tenant_id,
            operation_type: OPERATION_TYPE.SERVICE,
            certain_mode,
            tasks
        } as TOperation;

        const [{ est_start_time = Date.now() }] = activity_log || [];
        set({
            files,
            site,
            units: (site?.units || location?.units || []).reduce((rs: { [id: string]: IUnitWhenEdit }, item) => {
                const { id } = item;
                rs[id] = UnitModel(item, site || location);
                return rs;
            }, {}),
            startTime: est_start_time,
            operation: op,
            isCalculate: editMode === ACTION_TYPE.EDIT,
        })
    }, [set, get]);

    const { note = "" } = operation || {};
    return <div className='flex flex-col max-h-full overflow-auto'>
        <div className='flex gap-[5px] pr-[25px] justify-end items-center'>
            <AttachFile isShowList={false} />
            <AddNote
                isTooltip
                title='Operation'
                value={note}
                onChange={v => handeUpdateOp({ note: v })}
            />
            <Divider orientation='vertical' />
            <Certain
                value={operation?.certain_mode}
                onChange={certain_mode => handeUpdateOp({ certain_mode })}
            />
        </div>
        <div className='my-1 px-[20px]'>
            {(note || '').length > 0 && <Note {...{ isEdit: true, note }} handleUpdate={v => handeUpdateOp({ note: v })} />}
        </div>
        <div className='mt-2 mb-3 px-[20px]'>
            <DragDropProvider
                onDragEnd={event => {
                    if (event.canceled || !isSortableOperation(event.operation))
                        return;

                    const { source } = event.operation;
                    const activeIndex = source?.initialIndex;
                    const overIndex = source?.index;

                    if (activeIndex === undefined || overIndex === undefined || activeIndex === overIndex)
                        return;

                    set(({ operation }) => {
                        if (!operation)
                            return {};

                        const tasks = [...(operation.tasks || [])] as ITaskService[];

                        if (activeIndex < 0 || overIndex < 0)
                            return {};

                        const [task] = tasks.splice(activeIndex, 1);
                        tasks.splice(overIndex, 0, task);
                        return {
                            operation: { ...operation, tasks } as TOperation,
                            isCalculate: true,
                        };
                    });
                }}
            >
                <GroupTask tasks={operation?.tasks || []} />
            </DragDropProvider>
        </div>
        <div className='flex items-center justify-between mb-2'>
            <div className='flex gap-3 items-center'>
                <SelectTime />
                <Duration />
            </div>
            <div className='flex items-center pr-4 gap-4'>
                {operation &&
                    <AddChecklist data={operation} onChange={value => handeUpdateOp({ checklist_ids: value })} />}
            </div>
        </div>
        <div className='px-[20px] mb-3'>
            <ActivityLog tasks={operation?.tasks || []} trackDurationEst>
                <>
                    {(operation?.tasks || []).map((item: ITaskService) => {
                        const { type, name, duration } = item;
                        if (duration && type)
                            return <div key={item.group_id}>
                                {name ?? serviceTask[type]?.name} ({convertNumberTimeToStringTime(duration)})
                            </div>
                        return null
                    })}
                </>
            </ActivityLog>
        </div>
        <ContainerFooter />
    </div>;
};

export default Service;
